import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, MessageCircle, Eye } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { StatusBadge, fmtDate, inputCls, AreaF } from "./AFields";
import { Modal } from "./Participants";

const STATUSES = ["", "new", "viewed", "in_progress", "resolved", "archived"];

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    api.get(`/admin/enquiries/list?q=${encodeURIComponent(q)}&status=${status}`)
      .then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const update = async (eid, body) => {
    try {
      const r = await api.put(`/admin/enquiries/${eid}`, body);
      setItems(items.map((i) => (i.id === eid ? r.data : i)));
      if (open?.id === eid) setOpen(r.data);
      toast.success("Updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addNote = () => {
    if (!note.trim()) return;
    update(open.id, { notes: [...(open.notes || []), { text: note, at: new Date().toISOString() }] });
    setNote("");
  };

  return (
    <div data-testid="admin-enquiries">
      <SEO title="Enquiries" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Enquiries</h1>
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Name / Ref no…" className={`${inputCls} !w-64 !pl-10`} data-testid="enquiries-search" />
        </div>
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy" data-testid="enquiries-search-btn">Search</button>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !w-44`} data-testid="enquiries-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>
      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Ref No</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-b border-gray-100 last:border-0 hover:bg-navy-50/30" data-testid={`enquiry-row-${e.id}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{e.ref_no}</td>
                <td className="px-4 py-3 font-semibold text-navy">{e.name}</td>
                <td className="px-4 py-3 text-xs">{e.mobile}<br />{e.email}</td>
                <td className="px-4 py-3">{e.category}</td>
                <td className="px-4 py-3">{fmtDate(e.created_at)}</td>
                <td className="px-4 py-3">
                  <select value={e.status} onChange={(ev) => update(e.id, { status: ev.target.value })} className="rounded-md border border-gray-200 px-2 py-1 text-xs font-semibold" data-testid={`enquiry-status-${e.id}`}>
                    {STATUSES.filter(Boolean).map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => { setOpen(e); update(e.id, e.status === "new" ? { status: "viewed" } : {}); }} className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" data-testid={`enquiry-view-${e.id}`} aria-label="View"><Eye size={14} /></button>
                    {e.mobile && (
                      <a href={`https://wa.me/${e.mobile.replace(/\D/g, "")}?text=${encodeURIComponent(`Namaste ${e.name}, this is regarding your enquiry ${e.ref_no} at Sahaj Sarathi Foundation.`)}`} target="_blank" rel="noreferrer"
                        className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" data-testid={`enquiry-wa-${e.id}`} aria-label="WhatsApp"><MessageCircle size={14} /></a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No enquiries found.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal onClose={() => setOpen(null)} title={`${open.ref_no} — ${open.name}`} testid="enquiry-detail">
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-3"><StatusBadge value={open.status} /><span className="text-xs text-slate-400">{fmtDate(open.created_at)}</span></div>
            <p className="whitespace-pre-wrap rounded-lg bg-navy-50/50 p-4 text-slate-700">{open.message}</p>
            <div className="text-xs text-slate-500">{open.mobile} · {open.email} · {open.category}</div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Internal Notes</p>
              <ul className="space-y-2">
                {(open.notes || []).map((n, i) => <li key={i} className="rounded-md bg-saffron-100/50 px-3 py-2 text-xs text-slate-700">{n.text}</li>)}
              </ul>
              <div className="mt-3 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className={inputCls} data-testid="enquiry-note-input" />
                <button onClick={addNote} className="btn-dark !rounded-md !px-4 !py-2 text-xs" data-testid="enquiry-note-add">Add</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
